import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Upload, Typography, Alert, Segmented, Spin, Empty, Card } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { getUserType } from "../../../utils/appUtils";
import {
  fetchInvitationBuilderImageCategories,
  fetchInvitationBuilderImages,
} from "../../../redux/invitationSlice";
import { getInvitationBuilderLibraryImageLoadUrl } from "../../../utils/invitationBuilderLibraryImage";
import { isWebmUrl } from "../invitationMediaUtils";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r === "string") resolve(r);
      else reject(new Error("read"));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ImagesPanel({
  onImageUploaded,
  uploadLoading,
  sessionUploadImages = [],
  onAddSessionImageToCanvas,
  isLoggedIn,
}) {
  const dispatch = useDispatch();
  const guest = getUserType() === "guest" || !isLoggedIn;
  /** Selected slug; set to first API category once categories load (no "All"). */
  const [libraryCategory, setLibraryCategory] = useState(null);

  const builderImageCategories = useSelector(
    (s) => s.invitation.builderImageCategories
  );
  const fetchBuilderImageCategoriesLoading = useSelector(
    (s) => s.invitation.fetchBuilderImageCategoriesLoading
  );
  const builderImages = useSelector((s) => s.invitation.builderImages);
  const fetchBuilderImagesLoading = useSelector(
    (s) => s.invitation.fetchBuilderImagesLoading
  );

  useEffect(() => {
    dispatch(fetchInvitationBuilderImageCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!builderImageCategories?.length) return;
    setLibraryCategory((prev) =>
      prev != null ? prev : builderImageCategories[0].value
    );
  }, [builderImageCategories]);

  useEffect(() => {
    if (!libraryCategory) return;
    dispatch(fetchInvitationBuilderImages(libraryCategory));
  }, [dispatch, libraryCategory]);

  const segmentOptions = useMemo(
    () =>
      (builderImageCategories || []).map((c) => ({
        label: c.label,
        value: c.value,
      })),
    [builderImageCategories]
  );

  const props = {
    name: "file",
    multiple: false,
    showUploadList: false,
    beforeUpload: async (file) => {
      if (guest) return false;
      const base64 = await fileToBase64(file);
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const allowed = ["png", "jpg", "jpeg", "webp", "gif", "webm"];
      if (!allowed.includes(ext)) {
        return false;
      }
      onImageUploaded?.(base64, ext);
      return false;
    },
  };

  const awaitingFirstCategory =
    Boolean(builderImageCategories?.length) && libraryCategory == null;

  const libraryLoading =
    fetchBuilderImageCategoriesLoading ||
    awaitingFirstCategory ||
    (Boolean(libraryCategory) && fetchBuilderImagesLoading);

  return (
    <div className="invitation-builder__images-panel">
      {guest && (
        <Alert
          type="info"
          showIcon
          message="Sign in to upload images to the cloud"
          style={{ marginBottom: 12 }}
        />
      )}
      <Typography.Paragraph type="secondary">
        Upload a photo or logo. It will be placed on the canvas; drag to move
        and resize.
      </Typography.Paragraph>
      <Upload.Dragger {...props} disabled={guest || uploadLoading}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {guest ? "Sign in to upload" : "Click or drag image here"}
        </p>
        <p className="ant-upload-hint">PNG, JPG, WebP, GIF, WebM video</p>
      </Upload.Dragger>

      <Typography.Text strong className="invitation-builder__images-panel-library-heading">
        Library
      </Typography.Text>
      <Typography.Paragraph type="secondary" className="invitation-builder__images-panel-library-hint">
        Predefined images by category. Click to add to the canvas.
      </Typography.Paragraph>
      {segmentOptions.length > 0 && (
        <div className="invitation-builder__segmented-scroll invitation-builder__images-panel-segmented">
          <Segmented
            size="small"
            options={segmentOptions}
            value={libraryCategory ?? segmentOptions[0]?.value}
            onChange={setLibraryCategory}
            className="invitation-builder__segmented"
          />
        </div>
      )}
      {libraryLoading ? (
        <div className="invitation-builder__center invitation-builder__images-panel-library-loading">
          <Spin />
        </div>
      ) : !builderImageCategories?.length ? (
        <Empty
          className="invitation-builder__images-panel-library-empty"
          description="No image categories available"
        />
      ) : builderImages.length === 0 ? (
        <Empty
          className="invitation-builder__images-panel-library-empty"
          description="No images in this category"
        />
      ) : (
        <div className="invitation-builder__images-panel-library-grid">
          {builderImages.map((item) => {
            const loadUrl = getInvitationBuilderLibraryImageLoadUrl(item);
            const canAdd = Boolean(loadUrl);
            return (
              <Card
                key={`lib-${item.id}`}
                size="small"
                hoverable={canAdd}
                className={
                  canAdd
                    ? "invitation-builder__images-panel-library-card"
                    : "invitation-builder__images-panel-library-card invitation-builder__images-panel-library-card--disabled"
                }
                onClick={() => {
                  if (canAdd) onAddSessionImageToCanvas?.(loadUrl);
                }}
                cover={
                  loadUrl ? (
                    <div className="invitation-builder__images-panel-library-thumb-wrap">
                      {isWebmUrl(loadUrl) ? (
                        <video
                          aria-hidden
                          muted
                          playsInline
                          loop
                          autoPlay
                          src={loadUrl}
                          crossOrigin="anonymous"
                          className="invitation-builder__images-panel-library-thumb-video"
                        />
                      ) : (
                        <img
                          alt=""
                          src={loadUrl}
                          crossOrigin="anonymous"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="invitation-builder__images-panel-library-placeholder">
                      {item.category}
                    </div>
                  )
                }
              >
                <Card.Meta title={item.name} />
              </Card>
            );
          })}
        </div>
      )}

      {sessionUploadImages.length > 0 && (
        <>
          <Typography.Text
            type="secondary"
            className="invitation-builder__images-panel-uploaded-label"
          >
            Uploaded this session
          </Typography.Text>
          <div className="invitation-builder__images-panel-strip">
            {sessionUploadImages.map((item) => (
              <button
                key={item.key}
                type="button"
                className="invitation-builder__images-panel-thumb"
                onClick={() => onAddSessionImageToCanvas?.(item.url)}
                title="Add to canvas"
              >
                {isWebmUrl(item.url) ? (
                  <video
                    aria-hidden
                    muted
                    playsInline
                    loop
                    autoPlay
                    src={item.url}
                    className="invitation-builder__images-panel-thumb-video"
                  />
                ) : (
                  <img src={item.url} alt="" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
